import Icon from '@/components/icons/Icon';
import { IconProps } from '@/components/icons/types';
import { APP_ROUTES, ROUTE_BUILDER } from '@/constants/pages';
import { classnames, url } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import useHorizontalOverflowDetect from '@/hooks/useHorizontalOverflowDetect';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Fragment, useEffect, useRef, useState } from 'react';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { SEARCH_HEADER_MAX_HEIGHT } from '../search-header/SearchHeader';
import { SEARCH_CONTEXT } from '../searchV2/constants/tracking';
import {
  buildClientCategoryLink,
  buildSistaminutenLink,
  handleOnCategoryLinkClick,
  trackMegaMenuClick,
} from './helpers';
import { TabIdentifier } from './MegaMenu.constants';
import { UseMegaMenuManagerOutput } from './MegaMenu.types';
import Category from './modules/Category';

const baseTranslationKey = 'features.mega-menu.MegaMenu';

const MegaMenu = (props: UseMegaMenuManagerOutput) => {
  const tabsref = useRef<HTMLUListElement>(null);
  const history = useHistory();
  const routeMatch = useRouteMatch() as { params?: { location?: string } } | null;
  const location = useLocation();
  const screenName = getScreenNameFromPath(location.pathname);
  const isOnSerpRoute = useRouteMatch(APP_ROUTES.SERP) as {
    isExact: boolean;
    params: { q?: string; location?: string };
  } | null;
  const isOnSerpNoIndexRoute = useRouteMatch(APP_ROUTES.SERP_NO_INDEX);
  /**
   * if user navigates to sistaminuten serp from serp page, keep the current search parameters
   */
  let currentKeyword = undefined;
  let currentLocation = undefined;

  if (isOnSerpRoute?.isExact && screenName === 'search_results') {
    const { params } = isOnSerpRoute || {};
    currentKeyword = params.q;
    currentLocation = params.location;
  } else if (isOnSerpNoIndexRoute?.isExact && screenName === 'search_results') {
    currentKeyword = url.getParamsFromGET()['q'];
    currentLocation = url.getParamsFromGET()['location'];
  } else {
    currentLocation = routeMatch?.params?.location ?? url.getParamsFromGET()['location'];
  }

  const keywordWithSlug = getKeywordWithSlug(currentKeyword || '');
  let locationWithSlug = getLocationWithSlug(currentLocation || '');
  if (!locationWithSlug && (currentLocation || '').indexOf(', ') > 0) {
    locationWithSlug = getLocationWithSlug(currentLocation.split(', ').pop());
  }

  const { isMobileView } = useMobileView();
  const { isOverflowing, canScrollLeft, canScrollRight } = useHorizontalOverflowDetect(tabsref);

  return (
    <nav
      ref={props.navRef}
      className="relative w-full"
      aria-label={_s(`${baseTranslationKey}.aria-label`)}
      onMouseLeave={() => {
        const isTouchEnabledDevice = isMobile || isTablet;
        if (isTouchEnabledDevice) return;
        props.onNavigationLeave();
      }}>
      <div
        className={classnames(
          'pointer-events-none absolute left-0 top-0 z-10 flex h-full w-12 items-center justify-center transition-all duration-100',
          'bg-gradient-to-r from-white via-white/60 to-white/0',
          isOverflowing && canScrollLeft ? 'opacity-100' : 'opacity-0',
        )}
      />
      <ul
        ref={tabsref}
        className={classnames(
          'gap-x-lg no-scrollbar flex w-full items-center overflow-x-scroll min-[588px]:justify-between',
        )}>
        {(props.tabs ?? []).map((item) => {
          const isActive = props.activeTab === item.identifier || (screenName === item.identifier && !props.activeTab);

          /**
           * only keep sistaminuten tab as link on desktop view
           */
          if (isMobileView && item.identifier === TabIdentifier.SistaMinuten) {
            item.to = undefined;
          } else {
            /**
             * Keep search keyword and location if already on serp
             */
            if (item.identifier === TabIdentifier.SistaMinuten) {
              item.to = buildSistaminutenLink(keywordWithSlug?.slug, locationWithSlug?.slug);
            }
          }

          if (item.identifier === TabIdentifier.Deals) {
            item.to = isMobileView ? undefined : ROUTE_BUILDER.dealsLanding();
          }

          const btnClasses = classnames(
            'space-x-xs flex h-[56px] cursor-pointer items-center outline-none',
            isActive && 'tab-selected text-bg_primary',
            !isActive && 'text-fg_secondary',
          );

          const iconProps: IconProps = {
            variant: item.icon,
            color: (() => {
              if (item.identifier === TabIdentifier.SistaMinuten) {
                return 'bg_accent_5';
              }

              if (item.identifier === TabIdentifier.Deals) {
                return 'fg_highlight';
              }

              if (isActive) {
                return 'fg_primary';
              }

              return 'fg_secondary';
            })(),

            style: {
              filter: (() => {
                if (item.identifier === TabIdentifier.SistaMinuten) {
                  return 'var(--filter-color-bg-accent-5)';
                }

                if (item.identifier === TabIdentifier.Deals) {
                  return 'var(--filter-color-fg-highlight)';
                }

                if (isActive) {
                  return 'var(--filter-color-fg-primary)';
                }
                return 'var(--filter-color-fg-secondary)';
              })(),
              ...(isMobileView ? { width: 20, height: 20 } : { width: 24, height: 24 }),
            },
            className: (() => {
              if (item.identifier === TabIdentifier.SistaMinuten) {
                return '';
              }

              if (item.identifier === TabIdentifier.Deals) {
                return '';
              }

              // hide icon on mobile for other tabs
              return 'block min-[768px]:hidden';
            })(),
          };

          function handleOnClick(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) {
            props.onNavigationOpen(item.identifier);

            if (isMobileView && item.identifier !== props.activeTab) {
              props.onSubCategoryNavigationOpen(item.identifier, screenName);
            } else if (item.to) {
              trackMegaMenuClick(item.identifier, null, screenName);
            }
          }

          function handleOnMouseEnter() {
            if (props.activeTab === item.identifier) return;
            props.onMouseEnter(item.identifier, screenName);
          }

          const handlers = {
            onClick: handleOnClick,
            onMouseEnter: undefined,
            onMouseLeave: undefined,
          };

          if (isDesktop) {
            handlers['onMouseEnter'] = handleOnMouseEnter;
            handlers['onMouseLeave'] = props.onMouseLeave;
          }

          const children = (
            <Fragment>
              {item.icon && <Icon {...iconProps} />}
              <span data-label={item.label} className="text-m md:text-l">
                {item.label}
              </span>
            </Fragment>
          );

          return (
            <li
              key={item.identifier}
              className={classnames('relative flex-shrink-0', item.breakpoint ? `hidden ${item.breakpoint}` : '')}>
              {item.to && (
                <Fragment>
                  <Link
                    {...handlers}
                    className={classnames(btnClasses, 'focus:tab-selected focus:text-bg_primary')}
                    to={buildClientCategoryLink(
                      item.to,
                      routeMatch,
                      SEARCH_CONTEXT.TOP_NAVIGATION_BAR,
                      true,
                      locationWithSlug?.slug,
                    )}
                    onClick={(e) => {
                      /**
                       * if category is not open, open it
                       */
                      if (!props.activeTab) {
                        handlers.onClick(e);
                        return;
                      }

                      handleOnCategoryLinkClick(e, item.to, history, SEARCH_CONTEXT.TOP_NAVIGATION_BAR, () => {
                        props.onNavigationLeave(item.identifier);
                        trackMegaMenuClick(item.identifier, null, screenName);
                      });
                    }}>
                    {children}
                  </Link>
                  <button
                    {...handlers}
                    type="button"
                    className={classnames(
                      btnClasses,
                      'border-border_selected pointer-events-none absolute inset-0 focus:border-2',
                    )}
                    aria-controls={props.navigationContainerId}
                    aria-label={_s(`${baseTranslationKey}.category-toggle.aria-label.${item.identifier}`)}
                    aria-expanded={isActive}></button>
                </Fragment>
              )}
              {!item.to && (
                <button
                  {...handlers}
                  type="button"
                  className={btnClasses}
                  aria-controls={props.navigationContainerId}
                  aria-expanded={isActive}>
                  {children}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {props.renderNavigationContainer(props)}
      <div
        className={classnames(
          'pointer-events-none absolute right-0 top-0 flex h-full w-12 items-center justify-center transition-all duration-100',
          'bg-gradient-to-r from-white/0 via-white/60 to-white',
          isOverflowing && canScrollRight ? 'opacity-100' : 'opacity-0',
        )}
      />
    </nav>
  );
};

const MegaMenuCategories = (props: UseMegaMenuManagerOutput) => {
  const { activeTab, onNavigationLeave } = props;
  const { isMobileView } = useMobileView();
  const [showOverlay, setShowOverlay] = useState(false);
  const searchHeaderMaxHeight = SEARCH_HEADER_MAX_HEIGHT[isMobileView ? 'MOBILE' : 'DESKTOP'];

  /**
   * Styles for the mega menu categories to ensure they fit within the viewport height
   * while accounting for the search header and bottom navigation bar.
   * This uses the `100dvh` unit for better handling of dynamic viewport height changes
   * on mobile devices, falling back to `100vh` for older browsers.
   */
  const styles = `
  .mega-menu-categories-category {
    /* Fallback for older browsers (uses 100vh) */
    max-height: calc(100vh - ${searchHeaderMaxHeight}px - env(safe-area-inset-bottom) - env(safe-area-inset-top) - 60px);

    /* overrides the above if supported */
    max-height: calc(100dvh - ${searchHeaderMaxHeight}px - env(safe-area-inset-bottom) - env(safe-area-inset-top) - 60px);
  }
`;

  /**
   * Effect to manage the fade-in animation
   */
  useEffect(() => {
    if (activeTab) {
      const timer = setTimeout(() => {
        setShowOverlay(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setShowOverlay(false);
    }
  }, [activeTab]);

  return (
    <Fragment>
      <style>{styles}</style>
      {/* Add Overlay if category is open */}
      {activeTab && (
        <div
          onClick={() => props.onNavigationLeave()}
          onMouseEnter={() => onNavigationLeave()}
          className={classnames(
            'bg-overlay-50 fixed inset-0 -z-10 transition-opacity duration-300',
            showOverlay ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      {isMobileView && <Category.MobileNavigation {...props} />}
      <Category.Hairdresser {...props} />
      <Category.Massage {...props} />
      <Category.Nails {...props} />
      <Category.Lashes {...props} />
      <Category.Skincare {...props} />
      <Category.Beauty {...props} />
      <Category.Health {...props} />
      <Category.More {...props} />
      <Category.Wellnesscard {...props} />
      <Category.Giftcard {...props} />
      <Category.SistaMinuten {...props} />
    </Fragment>
  );
};

MegaMenu.Categories = MegaMenuCategories;

export default MegaMenu;
